import { AllyPoint } from "../types";
import { AllyBase } from "./base";

/**
 * This class creates a 2 dimensional matrix of callbacks that can be executed from a start point to an end point.
 * 
 * @extends AllyBase
 */
export class AllyFunctionMatrix<Type> extends AllyBase {
    public matrix: ((scope: Type & {[key: string]: unknown}) => void)[][];
    private matrixCopy: AllyFunctionMatrix<Type>["matrix"];

    /**
     * Creates a AllyFunctionMatrix object.
     * 
     * Provide the Type that is to be used for the scope via AllyFunctionMatrix<Type>. 
     * 
     * @param bridge The bridge to the parent in the hierarchical tree.
     * @param matrix The initial matrix.
     * @see AllyBase
     */
    constructor(bridge: () => AllyBase, matrix: ((scope: Type & {[key: string]: unknown}) => void)[][]) {
        super(bridge);
        this.matrix = matrix;
        this.matrixCopy = AllyFunctionMatrix.copy(this.matrix);
    }

    /**
     * This function executes the callbacks of  the functionMatrix starting at a start point and ending at an end point.
     *
     * During this execution the elements of a column are all executed then the execution continues at the top of the next column.
     * 
     * Note:
     * 
     * matrix[x][y] x is the column while y is the row.
     * 
     * The start of the matrix is at x = 0, the top of each column is at y = 0.
     * @param scope
     * The scope is an object that contains variables used in this instance of the run functions execution.
     * 
     * For each execution of a callback the same scope will be used.
     * @param options
     * An options object. - Available options:
     * 
     * start - the point in the functionMatrix where the execution of the callbacks saved in the functionMatrix starts.
     * 
     * end - the point in the functionMatrix where the execution of the callbacks saved in the functionMatrix ends.
     * 
     * Note: The x coordinate of the points is the column of the function matrix while y is the row.
     */
    public run(scope: Type & {[key: string]: unknown}, options?: {start?: AllyPoint, end?: AllyPoint}) {
        let start: AllyPoint = {};
        let end: AllyPoint = {};
        let curser: AllyPoint = {};
        
        start.x = options?.start?.x !== undefined ? options.start.x : 0;
        start.y = options?.start?.y !== undefined ? options.start.y : 0;
        end.x = options?.end?.x !== undefined ? options.end.x: this.matrix.length-1;
        let y = end.x < this.matrix.length ? end.x : this.matrix.length-1
        end.y = options?.end?.y !== undefined ? options.end.y : this.matrix[y].length-1;
        curser.x = start.x;
        curser.y = start.y;

        while (curser.x < this.matrix.length) {
            while (curser.y < this.matrix[curser.x].length) {
                this.matrix[curser.x][curser.y](scope);
                if (curser.x === end.x && curser.y === end.y) {
                    break;
                } else {
                    curser.y++;
                }
            }
            if (curser.x === end.x && curser.y === end.y) {
                break;
            } else {
                curser.x++;
            }
            curser.y = 0;
        }
    }

    /**
     * Creates a copy of the provided matrix.
     * @param matrix The matrix to be copied.
     * @returns Returns the created copy.
     */
    public static copy<Type>(matrix: AllyFunctionMatrix<Type>['matrix']) {
        let newMatrix: AllyFunctionMatrix<Type>['matrix'] = [];
        matrix.forEach((column) => {
            newMatrix.push([]);
            column.forEach((callback) => {
                newMatrix[newMatrix.length-1].push(callback);
            });
        });
        return newMatrix;
    }

    /**
     * Reset the matrix to the state it had when this AllyFunctionMatrix object was created.
     */
    public reset() {
        this.matrix = AllyFunctionMatrix.copy(this.matrixCopy);
    }
}